import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { useQuestionLibraryStore } from "../hooks/useQuestionLibraryStore";
import { BookOpen, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import CreateQuestionModal from "../components/CreateQuestionModal";
import QuestionList from "../components/QuestionList";
import QuestionSearchFilter from "../components/QuestionSearchFilter";

const QuestionLibraryPage = observer(() => {
  const { t } = useTran();
  const store = useQuestionLibraryStore();
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // ── Initialize on mount ───────────────────────────────────────────────
  useEffect(() => {
    store.initialize();
  }, []);

  // ── React to filter/page changes using MobX reaction ─────────────────
  // ✅ reaction() properly tracks MobX observables — useEffect deps do NOT
  useEffect(() => {
    if (!store.initializing) {
      store.fetchQuestions();
    }
  }, [
    store.pagination.page,
    store.filters.search,
    store.filters.category,
    store.filters.type,
    store.filters.sort,
    store.filters.showFavourites,
    store.filters.showArchived,
  ]);

  const { start, end, total } = store.paginationInfo;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("Question Library")}
        description={
          !store.initializing
            ? `${store.stats.total} questions · ${store.stats.categories} categories · ${store.stats.favourites} favourites`
            : t("Manage your question library.")
        }
        icon={BookOpen}
        actions={
          <Button
            type="button"
            size="sm"
            className="gap-2"
            onClick={store.openCreateModal}
          >
            <Plus className="h-4 w-4" />
            {t("Add New Question")}
          </Button>
        }
      />

      {/* ✅ Filter bar */}
      {!store.initializing && (
        <QuestionSearchFilter
          filters={store.filters} // ✅ pass whole filters
          onFilterChange={(key, value) => store.setFilter(key, value)}
        />
      )}

      {!store.initializing && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {total > 0
              ? `Showing ${start}–${end} of ${total}`
              : "No questions found"}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={!store.canGoPrev}
              onClick={store.goPrevPage}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!store.canGoNext}
              onClick={store.goNextPage}
              className="gap-1"
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <QuestionList
        questions={store.questions}
        loading={store.initializing || store.loading}
        selectedQuestion={selectedQuestion}
        onSelectQuestion={setSelectedQuestion}
        onCloseDetail={() => setSelectedQuestion(null)}
        onToggleFavourite={store.toggleFavourite}
        onEdit={store.openEditModal}
        onDuplicate={store.duplicateQuestion}
        onArchive={store.toggleArchive}
        onDelete={store.deleteQuestion}
        onCreateNew={store.openCreateModal}
      />

      <CreateQuestionModal
        isOpen={store.isModalOpen}
        isEditMode={store.isEditMode}
        editData={store.editingQuestion}
        submitting={store.submitting}
        onClose={store.closeModal}
        onSubmit={store.submitQuestion}
      />
    </div>
  );
});

export default QuestionLibraryPage;
